import React, { useState, useEffect } from "react";
import { Card, ListGroup, Dropdown, Spinner } from "react-bootstrap";
import styles from "./Card.module.css";
import GenericFHIRViewerModal from "../modals/ResourceViewerModal";
import { formatLocalDate } from "../utils/dateUtils";
import ExpandedIcon from "../assets/diagnostic-report-1.png";

function DiagnosticCard({
    title,
    inContext,
    reportData,
    fhirClient,
    patientId,
    enableSearch = false,
    onDiagnosticChange,
    isReply,
    depth = 6,
}) {
    const [error, setError] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [modalDiagnosticDetails, setModalDiagnosticDetails] = useState(null);
    const [performerName, setPerformerName] = useState("Loading...");
    const [diagnosticHistory, setDiagnosticHistory] = useState([]);
    const [selectedDiagnostic, setSelectedDiagnostic] = useState(reportData || null);
    const [historyLoading, setHistoryLoading] = useState(false);

    const handleModalClose = () => setShowModal(false);

    // Sync data
    useEffect(() => {
        if (reportData?.error) {
            setError(`Error loading diagnostic (code: ${reportData.error}).`);
            setSelectedDiagnostic(null);
        } else {
            setError("");
            setSelectedDiagnostic(reportData || null);
        }
    }, [reportData]);

    // Performer
    useEffect(() => {
        const fetchPerformer = async () => {
            const ref = selectedDiagnostic?.performer?.[0]?.reference;

            if (!fhirClient || !ref || !ref.includes("/") || ref.endsWith("/undefined")) {
                setPerformerName("N/A");
                return;
            }
            try {
                const data = await fhirClient.request(ref);
                setPerformerName(data?.name || data?.display || "Unknown Performer");
            } catch {
                setPerformerName("N/A");
            }
        };

        if (selectedDiagnostic) fetchPerformer();
        else setPerformerName("N/A");
    }, [selectedDiagnostic, fhirClient]);

    // History fetch
    useEffect(() => {
        if (!enableSearch || !patientId || !fhirClient) return;

        const fetchDiagnostics = async () => {
            setHistoryLoading(true);
            try {
                const response = await fhirClient.request(
                    `DiagnosticReport?patient=Patient/${patientId}&_sort=-date&_count=10`
                );
                const found = response.entry?.map((e) => e.resource) || [];
                setDiagnosticHistory(found);
            } catch {
                setDiagnosticHistory([]);
            } finally {
                setHistoryLoading(false);
            }
        };

        fetchDiagnostics();
    }, [enableSearch, patientId, fhirClient]);

    useEffect(() => {
        if (!isReply && enableSearch) {
            if (diagnosticHistory.length > 0) {
                const first = diagnosticHistory[0];
                setSelectedDiagnostic(first);
                if (onDiagnosticChange) onDiagnosticChange(first);
            } else {
                setSelectedDiagnostic(null);
                if (onDiagnosticChange) onDiagnosticChange(null);
            }
        }
    }, [diagnosticHistory]);

    // Modal loader
    const handleModalOpen = async () => {
        if (!selectedDiagnostic?.id) return;

        try {
            const details = await fhirClient.request(
                `DiagnosticReport/${selectedDiagnostic.id}`
            );
            setModalDiagnosticDetails(details);
        } catch {
            setModalDiagnosticDetails(null);
        }

        setShowModal(true);
    };

    const handleSelectDiagnostic = (dr) => {
        setSelectedDiagnostic(dr);
        if (onDiagnosticChange) onDiagnosticChange(dr);
    };

    const fieldRenderers = [
        <ListGroup.Item className={styles.listItem} key="code">
            <strong>Code:</strong> {selectedDiagnostic?.code?.text || "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="status">
            <strong>Status:</strong> {selectedDiagnostic?.status || "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="performer">
            <strong>Performer:</strong> {performerName}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="effective">
            <strong>Effective:</strong>{" "}
            {formatLocalDate(
                selectedDiagnostic?.effectiveDateTime ||
                selectedDiagnostic?.effectivePeriod?.start
            )}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="issued">
            <strong>Issued:</strong> {formatLocalDate(selectedDiagnostic?.issued)}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="results">
            <strong>Results:</strong>{" "}
            {selectedDiagnostic?.result
                ?.map((r) => r.reference.replace("Observation/", "Obs #"))
                .join(", ") || "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="conclusion">
            <strong>Conclusion:</strong> {selectedDiagnostic?.conclusion || "N/A"}
        </ListGroup.Item>,
    ];

    return (
        <>
            <Card className={styles.shrinkCard}>
                <Card.Header
                    className={`d-flex justify-content-between align-items-center ${
                        inContext ? styles.inContextCardHeader : styles.cardHeader
                    }`}
                    onClick={handleModalOpen}
                    style={{ cursor: "pointer" }}
                >
                    {/* LEFT: icon + title (DO NOT block clicks) */}
                    <div className="d-flex align-items-center" style={{ pointerEvents: "none" }}>
                        <img
                            src={ExpandedIcon}
                            alt="Diagnostic Icon"
                            className={styles.fhirIcon}
                        />

                        <h6 className={`${styles.titleText} mb-0`}>
                            {title?.length > 75
                                ? `Diagnostic Report: ${title.substring(0, 75)}…`
                                : `Diagnostic Report: ${title}`}
                        </h6>
                    </div>

                    {/* RIGHT: dropdown (STOP header click) */}
                    <div
                        className="d-flex align-items-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {depth > 0 && enableSearch && !isReply && (
                            historyLoading ? (
                                <Spinner animation="border" role="status" size="sm" className="me-2">
                                    <span className="visually-hidden">Loading...</span>
                                </Spinner>
                            ) : (
                                <Dropdown
                                    onSelect={(eventKey) =>
                                        handleSelectDiagnostic(
                                            diagnosticHistory.find((d) => d.id === eventKey)
                                        )
                                    }
                                >
                                    <Dropdown.Toggle variant="outline-primary" size="sm">
                                        Select Report
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu style={{ maxHeight: "300px", overflowY: "auto" }}>
                                        {diagnosticHistory.map((dr) => (
                                            <Dropdown.Item key={dr.id} eventKey={dr.id}>
                                                {dr.code?.text || "Report"} ·{" "}
                                                {formatLocalDate(dr.effectiveDateTime)}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            )
                        )}
                    </div>
                </Card.Header>

                {depth > 0 && (
                    <Card.Body>
                        {error ? (
                            <p className={styles.errorText}>{error}</p>
                        ) : (
                            <ListGroup variant="flush">
                                {fieldRenderers.slice(0, depth)}
                            </ListGroup>
                        )}
                    </Card.Body>
                )}
            </Card>

            <GenericFHIRViewerModal
                show={showModal}
                onClose={handleModalClose}
                resource={modalDiagnosticDetails}
                //title={selectedDiagnostic?.code?.text || "Diagnostic Report"}
                fhirClient={fhirClient}
                //icon={ExpandedIcon}
            />
        </>
    );
}

export default DiagnosticCard;
